
/** One icon's rectangle in the sheet, in atlas pixels. */
export interface IconCell {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** What both manifests carry: an image and the rectangles cut out of it. */
export interface SpriteSheet {
  /** The sheet, named relative to the manifest. */
  readonly image: string;
  readonly width: number;
  readonly height: number;
  /** `[x, y, w, h]` per cell. */
  readonly cells: readonly (readonly number[])[];
}

/** `client/public/item_icons.json`, exactly as the tool writes it. */
export interface ItemIconManifest extends SpriteSheet {
  /** Item vnum (as a string, because JSON) to an index into `cells`. */
  readonly items: Readonly<Record<string, number>>;
}

/** `client/public/skill_icons.json`, exactly as `build_skill_icons.py` writes it. */
export interface SkillIconManifest extends SpriteSheet {
  /** Icon name -- `SkillSlot.icon` off the wire -- to one cell index per grade,
   *  `[normal, master, grand master]`. */
  readonly skills: Readonly<Record<string, readonly number[]>>;
}

export const ITEM_ICON_MANIFEST_URL = "item_icons.json";

const STYLE_ID = "item-icon-style";
const CLASS = "item-icon";

const STYLE = `
.${CLASS} {
  display: block;
  margin: auto;
  height: 94%;
  aspect-ratio: var(--item-icon-ar);
  max-width: 100%;
  background-image: var(--item-icon-sheet);
  background-repeat: no-repeat;
  background-size: var(--item-icon-size);
  background-position: var(--item-icon-pos);
  image-rendering: pixelated;
}
`;

/** The loaded sheet, or null while there is none. */
let sheet: { readonly manifest: ItemIconManifest; readonly url: string } | null = null;

function installStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID) !== null) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = STYLE;
  doc.head.append(style);
}

function usable(value: unknown): value is ItemIconManifest {
  if (!sheetUsable(value)) return false;
  const m = value as Partial<ItemIconManifest>;
  return typeof m.items === "object" && m.items !== null;
}

function sheetUsable(value: unknown): value is SpriteSheet {
  if (typeof value !== "object" || value === null) return false;
  const m = value as Partial<SpriteSheet>;
  if (typeof m.image !== "string" || m.image.length === 0) return false;
  if (!Number.isFinite(m.width) || !Number.isFinite(m.height)) return false;
  if ((m.width as number) <= 0 || (m.height as number) <= 0) return false;
  if (!Array.isArray(m.cells) || m.cells.length === 0) return false;
  for (const cell of m.cells) {
    if (!Array.isArray(cell) || cell.length !== 4) return false;
    for (const n of cell) if (!Number.isFinite(n)) return false;
    // A cell as wide or as tall as the sheet would make the percentage
    // background-position a division by zero, and there is no such icon.
    if (cell[2] <= 0 || cell[3] <= 0) return false;
    if (cell[2] >= (m.width as number) || cell[3] >= (m.height as number)) return false;
    if (cell[0] < 0 || cell[1] < 0) return false;
    if (cell[0] + cell[2] > (m.width as number)) return false;
    if (cell[1] + cell[3] > (m.height as number)) return false;
  }
  return true;
}

export function setItemIcons(manifest: unknown, imageUrl = ""): boolean {
  if (manifest === null) {
    sheet = null;
    return false;
  }
  if (!usable(manifest)) return false;
  sheet = { manifest, url: imageUrl };
  return true;
}

/** Fetch the manifest. Resolves to whether icons are now available; never rejects. */
export async function loadItemIcons(url: string = ITEM_ICON_MANIFEST_URL): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
    const manifest: unknown = await response.json();
    const image =
      typeof manifest === "object" && manifest !== null
        ? String((manifest as ItemIconManifest).image)
        : "";
    // Resolved against the manifest rather than against the page: both live in
    // `public/`, and a client served from a sub-path must not go looking at the
    // origin's root for the sheet.
    const imageUrl = new URL(image, new URL(url, document.baseURI)).href;
    if (!setItemIcons(manifest, imageUrl)) throw new Error("manifest is not the shape this reads");
    return true;
  } catch (error) {
    // Not fatal, and said once. Every panel draws its class glyphs instead.
    console.warn(`item icons unavailable (${String(error)}); drawing class glyphs`);
    sheet = null;
    return false;
  }
}

/** The rectangle for an item, or null when this build has no painting for it. */
export function itemIconCell(vnum: number): IconCell | null {
  if (sheet === null || !Number.isInteger(vnum)) return null;
  const index = sheet.manifest.items[String(vnum)];
  if (typeof index !== "number") return null;
  const cell = sheet.manifest.cells[index];
  if (cell === undefined) return null;
  return { x: cell[0], y: cell[1], w: cell[2], h: cell[3] };
}

export function iconCellsTall(vnum: number): number {
  const cell = itemIconCell(vnum);
  if (cell === null) return 1;
  return cellsTall(cell.h);
}

/** Whole inventory cells for a packed height. Shared by the item lookup and
 *  by `paint`, which has a cell but not a vnum. */
function cellsTall(height: number): number {
  return Math.max(1, Math.round(height / 32));
}

/** Whether anything at all is loaded. Only for callers deciding what to log. */
export function itemIconsReady(): boolean {
  return sheet !== null;
}

export function applyItemIcon(element: HTMLElement, vnum: number): boolean {
  const cell = itemIconCell(vnum);
  if (cell === null || sheet === null) return false;
  installStyle(element.ownerDocument);
  element.classList.add(CLASS);
  paint(element, "--item-icon", cell, sheet.manifest, sheet.url);
  return true;
}

const CROP_INSET_PX = 0.5;

/** The packed cell, shrunk by `CROP_INSET_PX` on every side. */
function cropCell(cell: IconCell): IconCell {
  const inset = Math.min(CROP_INSET_PX, cell.w / 4, cell.h / 4);
  return {
    x: cell.x + inset,
    y: cell.y + inset,
    w: cell.w - 2 * inset,
    h: cell.h - 2 * inset,
  };
}

function paint(
  element: HTMLElement,
  prefix: string,
  cell: IconCell,
  { width, height }: SpriteSheet,
  url: string,
): void {
  const crop = cropCell(cell);
  const style = element.style;
  style.setProperty(`${prefix}-sheet`, `url("${url}")`);
  style.setProperty(`${prefix}-ar`, `32 / ${String(32 * cellsTall(cell.h))}`);
  style.setProperty(
    `${prefix}-size`,
    `${pct((width / crop.w) * 100)} ${pct((height / crop.h) * 100)}`,
  );
  style.setProperty(
    `${prefix}-pos`,
    `${pct((crop.x / (width - crop.w)) * 100)} ${pct((crop.y / (height - crop.h)) * 100)}`,
  );
}

export function itemIconNode(vnum: number, doc: Document = document): HTMLElement | null {
  if (itemIconCell(vnum) === null) return null;
  const span = doc.createElement("span");
  span.setAttribute("aria-hidden", "true");
  return applyItemIcon(span, vnum) ? span : null;
}

/** Percentages, rounded rather than printed at full float width. Four decimals
 *  is a fifth of a pixel on a 512-wide sheet, and it keeps the inline style
 *  short enough to read in a devtools inspector. */
function pct(value: number): string {
  return `${String(Math.round(value * 1e4) / 1e4)}%`;
}

// ---------------------------------------------------------------------------
// Skill icons
// ---------------------------------------------------------------------------

export const SKILL_ICON_MANIFEST_URL = "skill_icons.json";

const SKILL_STYLE_ID = "skill-icon-style";
const SKILL_CLASS = "skill-icon";

/** Fills its box rather than being letterboxed into it: every skill icon is
 *  square, so there is no aspect ratio to preserve against a square button and
 *  nothing is stretched. `--skill-icon-ar` is still written, so a caller that
 *  wants the intrinsic shape has it. */
const SKILL_STYLE = `
.${SKILL_CLASS} {
  display: block;
  background-image: var(--skill-icon-sheet);
  background-repeat: no-repeat;
  background-size: var(--skill-icon-size);
  background-position: var(--skill-icon-pos);
  image-rendering: pixelated;
}
`;

let skillSheet: { readonly manifest: SkillIconManifest; readonly url: string } | null = null;

function installSkillStyle(doc: Document): void {
  if (doc.getElementById(SKILL_STYLE_ID) !== null) return;
  const style = doc.createElement("style");
  style.id = SKILL_STYLE_ID;
  style.textContent = SKILL_STYLE;
  doc.head.append(style);
}

function skillsUsable(value: unknown): value is SkillIconManifest {
  if (!sheetUsable(value)) return false;
  const m = value as Partial<SkillIconManifest>;
  if (typeof m.skills !== "object" || m.skills === null) return false;
  for (const grades of Object.values(m.skills)) {
    if (!Array.isArray(grades) || grades.length !== SKILL_GRADES) return false;
    for (const index of grades) {
      if (!Number.isInteger(index)) return false;
      if (index < 0 || index >= (m.cells as readonly unknown[]).length) return false;
    }
  }
  return true;
}

/** `SKILL_GRADE_COUNT` (`UserInterface/PythonSkill.h`): normal, master, grand
 *  master. Perfect master is a fourth grade with no fourth picture. */
export const SKILL_GRADES = 3;

export function skillGradeFor(level: number): number {
  if (!Number.isFinite(level) || level < 20) return 0;
  if (level < 30) return 1;
  return 2;
}

/** Install a skill manifest. Same contract as `setItemIcons`. */
export function setSkillIcons(manifest: unknown, imageUrl = ""): boolean {
  if (manifest === null) {
    skillSheet = null;
    return false;
  }
  if (!skillsUsable(manifest)) return false;
  skillSheet = { manifest, url: imageUrl };
  return true;
}

/** Fetch the skill manifest. Resolves to whether icons are now available; never rejects. */
export async function loadSkillIcons(url: string = SKILL_ICON_MANIFEST_URL): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
    const manifest: unknown = await response.json();
    const image =
      typeof manifest === "object" && manifest !== null
        ? String((manifest as SkillIconManifest).image)
        : "";
    const imageUrl = new URL(image, new URL(url, document.baseURI)).href;
    if (!setSkillIcons(manifest, imageUrl)) throw new Error("manifest is not the shape this reads");
    return true;
  } catch (error) {
    console.warn(`skill icons unavailable (${String(error)}); drawing names only`);
    skillSheet = null;
    return false;
  }
}

/** The rectangle for a skill's icon at a grade, or null when there is none. */
export function skillIconCell(icon: string, grade = 0): IconCell | null {
  if (skillSheet === null || typeof icon !== "string" || icon.length === 0) return null;
  const grades = skillSheet.manifest.skills[icon];
  if (grades === undefined) return null;
  // Clamped rather than rejected: a grade past the end is the original's own
  // perfect-master case, which draws the last picture there is.
  const index = grades[Math.min(Math.max(grade, 0), grades.length - 1)];
  const cell = skillSheet.manifest.cells[index];
  if (cell === undefined) return null;
  return { x: cell[0], y: cell[1], w: cell[2], h: cell[3] };
}

/** Whether the skill sheet is loaded at all. */
export function skillIconsReady(): boolean {
  return skillSheet !== null;
}

/** Point an existing element at a skill icon. False and untouched when there is none. */
export function applySkillIcon(element: HTMLElement, icon: string, grade = 0): boolean {
  const cell = skillIconCell(icon, grade);
  if (cell === null || skillSheet === null) return false;
  installSkillStyle(element.ownerDocument);
  element.classList.add(SKILL_CLASS);
  paint(element, "--skill-icon", cell, skillSheet.manifest, skillSheet.url);
  return true;
}

/** A ready-made icon node for a skill, or null when this build has none. */
export function skillIconNode(
  icon: string,
  grade = 0,
  doc: Document = document,
): HTMLElement | null {
  if (skillIconCell(icon, grade) === null) return null;
  const span = doc.createElement("span");
  span.setAttribute("aria-hidden", "true");
  return applySkillIcon(span, icon, grade) ? span : null;
}
